export interface GuideSection {
  heading: string;
  paragraphs: string[];
  points?: string[];
}

export interface GuideItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  cover: string;
  coverAlt: string;
  tags: string[];
  intro: string;
  sections: GuideSection[];
}

export const guideCategories = ["全部", "核验方法", "证据判断", "官方信息", "传播伦理"];

export const guides: GuideItem[] = [
  {
    id: "guide-1",
    slug: "three-steps-to-verify-a-rumor",
    title: "遇到疑似谣言，普通人如何用三步完成初步核验",
    excerpt:
      "不急着转发，也不急着下结论。用“找源头、对时间、看回应”三步，先把信息从情绪里剥离出来。",
    category: "核验方法",
    readTime: "6 分钟",
    date: "2026-08-20",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20still%20life%20photograph%20of%20an%20open%20notebook%20with%20a%20pen%2C%20a%20magnifying%20glass%20and%20a%20smartphone%20arranged%20on%20a%20warm%20wooden%20desk%2C%20soft%20natural%20window%20light%2C%20muted%20earthy%20tones%2C%20calm%20methodical%20mood%2C%20no%20readable%20text%2C%20consistent%20verification%20guide%20visual%20style%2C%20high%20detail%2C%20clean%20composition&width=1200&height=750&seq=guide-step-01&orientation=landscape",
    coverAlt: "三步核验方法示意：笔记、放大镜与手机",
    tags: ["初步核验", "信息溯源", "求证清单"],
    intro:
      "很多传言之所以能传播，不是因为它看起来真实，而是因为它先调动了情绪。三步核验的核心，是先把情绪放到一边，再回到可以核对的事实上。",
    sections: [
      {
        heading: "第一步：先找源头，而不是先看转发",
        paragraphs: [
          "一条信息在传播过程中会被反复改写，标题越来越夸张，细节越来越完整，却离原始信息越来越远。核验的第一步，是找到它最早出现的地方，而不是停留在你看到的那一条。",
          "如果在群聊或转发中看到，可以先向上追问：这是谁发的，原始链接在哪里，最初配文是什么。很多时候你会发现，最早的版本并没有后来那些确定的说法。",
        ],
        points: [
          "优先寻找原始链接、原始视频或原始截图",
          "记录你看到它的时间与平台",
          "注意标题与正文是否一致",
        ],
      },
      {
        heading: "第二步：对照时间与场景",
        paragraphs: [
          "时间错位是最常见的误导方式。一张旧照片、一段旧视频，只要换一个时间点，就足以让人产生完全不同的理解。对时间时，要特别关注信息里出现的日期、季节、天气与具体地点。",
          "场景同样重要。同一段画面，被放在不同的地点就可能是另一个故事。可以借助画面中的建筑、路牌、店铺等细节，判断它是否与所述地点相符。",
        ],
        points: ["核对发布时间与信息中提到的日期是否一致", "留意季节、天气等容易被忽略的细节"],
      },
      {
        heading: "第三步：查有没有官方回应",
        paragraphs: [
          "涉及公共事务的信息，往往会有主管部门、运营方或当事机构的公开说明。核验时可以把这些回应找出来，重点看回应的时间、主体和具体结论，而不是只看一句表态。",
          "需要提醒的是，官方回应本身也需要核对。要看是不是来自可确认的官方渠道，而不是被拼接、伪造的截图。",
        ],
        points: ["确认回应主体是否为可核实的官方渠道", "关注回应中是否给出具体事实与处理进展"],
      },
    ],
  },
  {
    id: "guide-2",
    slug: "how-to-judge-screenshot-credibility",
    title: "聊天截图可信度怎么判断？先看这五个细节",
    excerpt:
      "截图是最容易被修改的证据形式。看清状态栏、时间、头像链路与上下文，能过滤掉相当一部分伪造信息。",
    category: "证据判断",
    readTime: "5 分钟",
    date: "2026-08-06",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20close-up%20photograph%20of%20a%20smartphone%20screen%20resting%20on%20a%20plain%20neutral%20surface%20beside%20a%20sheet%20of%20paper%2C%20soft%20diffused%20daylight%2C%20muted%20gray%20and%20cream%20tones%2C%20calm%20analytical%20mood%2C%20screen%20intentionally%20blank%20with%20no%20readable%20text%2C%20consistent%20verification%20guide%20visual%20style%2C%20high%20detail%2C%20minimal%20composition&width=1200&height=750&seq=guide-screenshot-02&orientation=landscape",
    coverAlt: "聊天截图可信度判断示意",
    tags: ["截图核验", "证据链", "细节比对"],
    intro:
      "截图并不等于证据。它的优势是直观，劣势是极易被裁剪与拼接。判断一张截图是否可信，可以从可被验证的细节入手。",
    sections: [
      {
        heading: "看界面细节是否自洽",
        paragraphs: [
          "不同平台、不同系统的界面元素存在差异。状态栏、时间显示、消息气泡、输入框的位置，都是可以对照的线索。若一张截图里同时出现了彼此矛盾的系统元素，就需要提高警惕。",
        ],
        points: [
          "状态栏图标与时间是否一致",
          "消息气泡样式与所述平台是否匹配",
          "是否存在明显的边缘错位或重复纹理",
        ],
      },
      {
        heading: "看上下文是否完整",
        paragraphs: [
          "单条消息可以表达任何意思，而一段完整对话往往能还原语境。如果截图只保留了最关键的一两句，且刻意裁掉了前后文，就需要追问完整记录。",
        ],
        points: ["是否缺少前后对话", "发言者身份是否可核实"],
      },
      {
        heading: "看有没有可对照的其他证据",
        paragraphs: [
          "一张截图很难单独成立。若截图所述的事实在其他渠道也有旁证，可信度会明显提高；若所有信息都只来自同一张截图，则应保持谨慎。",
        ],
      },
    ],
  },
  {
    id: "guide-3",
    slug: "key-points-in-official-statements",
    title: "读官方通报要抓哪些关键信息？别只记住一句表态",
    excerpt:
      "官方回应里有三类信息最值得关注：谁在回应、回应了什么事实、下一步怎么做。",
    category: "官方信息",
    readTime: "5 分钟",
    date: "2026-07-22",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20documentary%20photograph%20of%20an%20official%20notice%20board%20and%20document%20folders%20on%20a%20clean%20desk%20under%20soft%20daylight%2C%20muted%20warm%20neutral%20tones%2C%20calm%20orderly%20mood%2C%20no%20readable%20text%20or%20logos%2C%20consistent%20verification%20guide%20visual%20style%2C%20high%20detail%2C%20balanced%20composition&width=1200&height=750&seq=guide-statement-03&orientation=landscape",
    coverAlt: "官方通报信息阅读要点示意",
    tags: ["官方回应", "信息解读", "通报要点"],
    intro:
      "官方回应是核验中非常重要的一环，但回应也有层级与范围。学会抓关键信息，可以避免把一句表态当成最终结论。",
    sections: [
      {
        heading: "先看是谁在回应",
        paragraphs: [
          "回应主体的层级与职责，直接决定这条回应的适用范围。主管部门的通报、当事机构的说明、运营方的告知，各自回应的问题并不相同。",
        ],
        points: ["区分主管部门与当事方", "确认发布渠道是否为可核实的官方渠道"],
      },
      {
        heading: "再看回应了哪些事实",
        paragraphs: [
          "有价值的回应通常会给出具体事实，例如检测结果、时间节点、处理进展。若回应只停留在“高度重视”“正在核实”，说明事件仍处在早期阶段。",
        ],
      },
      {
        heading: "最后看下一步安排",
        paragraphs: [
          "通报中的后续安排，往往比当前的结论更能说明事件走向。关注是否给出时间表、责任主体与公开方式，有助于判断信息是否会继续更新。",
        ],
      },
    ],
  },
  {
    id: "guide-4",
    slug: "spot-common-video-editing-tricks",
    title: "视频爆料里的常见剪辑手法，怎么识别",
    excerpt:
      "画面跳变、声音断层、时间线错位——这三种信号，是识别剪辑拼接最实用的切入点。",
    category: "证据判断",
    readTime: "7 分钟",
    date: "2026-06-30",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20video%20editing%20timeline%20software%20glowing%20on%20a%20monitor%20in%20a%20dim%20studio%2C%20muted%20cool%20gray%20with%20warm%20desk%20lamp%20accent%2C%20calm%20focused%20mood%2C%20screen%20interface%20intentionally%20blurred%20with%20no%20readable%20text%2C%20consistent%20verification%20guide%20visual%20style%2C%20high%20detail%2C%20cinematic%20composition&width=1200&height=750&seq=guide-video-04&orientation=landscape",
    coverAlt: "视频剪辑痕迹识别示意",
    tags: ["视频核验", "剪辑识别", "原始素材"],
    intro:
      "短视频是爆料最常见的载体，也是最容易通过剪辑改变含义的载体。识别剪辑并不是要成为专业人士，而是掌握几个可观察的信号。",
    sections: [
      {
        heading: "画面跳变：前后帧不连续",
        paragraphs: [
          "剪辑点通常表现为画面突然位移、光线变化或人物姿态不连贯。把视频放慢播放，逐段观察，比较容易发现这些不连续的地方。",
        ],
        points: ["放慢播放观察衔接处", "注意光线与角度的突然变化"],
      },
      {
        heading: "声音断层：环境音被切断",
        paragraphs: [
          "环境音往往具有连续性，一旦在某个位置突然中断或明显变调，说明此处很可能是拼接点。关闭画面只听声音，有时反而更容易发现问题。",
        ],
        points: ["单独听音频寻找断层", "对比口型与声音节奏是否吻合"],
      },
      {
        heading: "时间线错位：画面与所述时间不符",
        paragraphs: [
          "即使画面本身没有被剪辑，也可能因为被放在错误的时间点而产生误导。核验时要把画面中的可识别信息与所述时间对照。",
        ],
      },
    ],
  },
  {
    id: "guide-5",
    slug: "how-to-trace-original-source",
    title: "如何追溯一条信息的原始出处",
    excerpt:
      "把“别人说的”变回“谁最早说的”，是判断信息可靠性的关键一步。",
    category: "核验方法",
    readTime: "6 分钟",
    date: "2026-06-12",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20still%20life%20photograph%20of%20a%20long%20paper%20trail%20of%20printed%20pages%20connected%20by%20thin%20string%20across%20a%20desk%2C%20soft%20warm%20daylight%2C%20muted%20neutral%20tones%2C%20calm%20investigative%20mood%2C%20no%20readable%20text%2C%20consistent%20verification%20guide%20visual%20style%2C%20high%20detail%2C%20thoughtful%20composition&width=1200&height=750&seq=guide-trace-05&orientation=landscape",
    coverAlt: "信息溯源过程示意",
    tags: ["信息溯源", "原始出处", "传播链"],
    intro:
      "信息在传播中会不断丢失来源。追溯原始出处的过程，本质上是在重建一条传播链。",
    sections: [
      {
        heading: "从最近的转发逐级向上",
        paragraphs: [
          "从你看到的那一条开始，向上一级一级追问，直到找到一个不再引用他人的发布者。这个“不再引用他人”的位置，通常就是当前可追溯的起点。",
        ],
        points: ["记录每一级的发布时间", "留意配文是否被反复改写"],
      },
      {
        heading: "用关键句反查早期版本",
        paragraphs: [
          "把信息中最具体、最不可能被改写的一句话提取出来，作为检索线索反查更早的版本，往往能找到最初的表述。",
        ],
      },
      {
        heading: "判断起点是否具备可信度",
        paragraphs: [
          "找到起点之后，还需要判断这个起点本身是否可信。是亲历者的原始记录，还是二次加工的转述，可靠性并不相同。",
        ],
      },
    ],
  },
  {
    id: "guide-6",
    slug: "how-to-verify-without-second-spread",
    title: "求证时如何避免二次传播",
    excerpt:
      "求证不等于转发。用打码、脱敏与不扩散原素材的方式提问，既保护他人，也避免放大未证实信息。",
    category: "传播伦理",
    readTime: "5 分钟",
    date: "2026-05-16",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20still%20life%20photograph%20of%20a%20hand%20shielding%20a%20blank%20smartphone%20screen%20beside%20a%20small%20plant%20on%20a%20calm%20desk%2C%20soft%20gentle%20daylight%2C%20muted%20green%20and%20cream%20tones%2C%20quiet%20considerate%20mood%2C%20no%20readable%20text%2C%20consistent%20verification%20guide%20visual%20style%2C%20high%20detail%2C%20minimal%20composition&width=1200&height=750&seq=guide-ethics-06&orientation=landscape",
    coverAlt: "求证过程中的信息保护示意",
    tags: ["传播伦理", "隐私保护", "求证方式"],
    intro:
      "求证的过程如果处理不当，很可能反而扩大了未证实信息的传播。谨慎的求证方式，值得被当成一种习惯。",
    sections: [
      {
        heading: "求证时不直接扩散原素材",
        paragraphs: [
          "在向他人核实信息时，尽量不要完整转发可能涉及隐私或未证实的原始素材。可以先描述概要，再询问对方是否了解情况。",
        ],
        points: ["避免完整转发可疑素材", "对可识别信息进行打码脱敏"],
      },
      {
        heading: "给结论保留空间",
        paragraphs: [
          "求证阶段的事实往往并不完整，因此表达时可以使用“暂未确认”“有待核实”等表述，避免在不充分的信息上下定论。",
        ],
      },
      {
        heading: "澄清时也需要注意方式",
        paragraphs: [
          "当信息被证实为不实，引导他人回到可核实的来源，往往比简单否定更有效。提供核验依据，比情绪化的反驳更有说服力。",
        ],
      },
    ],
  },
];

export const getGuideBySlug = (slug: string): GuideItem | undefined =>
  guides.find((item) => item.slug === slug);

export const getGuidesByIds = (ids: string[]): GuideItem[] =>
  guides.filter((item) => ids.includes(item.id));